import { useId, type SelectHTMLAttributes } from "react";
import { AlertCircle } from "lucide-react";
import { Dica } from "./Dica";

/*
 * Seleção nativa com rótulo, dica e erro — o par do `Campo` para `<select>`.
 *
 * Nativa, e não o Select do Radix: as listas daqui têm de meia dúzia a trinta
 * opções, e o `<select>` do navegador já resolve teclado, leitor de tela, celular
 * e digitar o início do rótulo, sem um portal a mais (design D17). É a mesma
 * escolha do filtro de situação em Boletos.
 *
 * `grupos` vira `<optgroup>`, depois das `opcoes` soltas: o catálogo separa os
 * bancos que emitem dos que não têm ficha, sem criar uma seleção à parte.
 */
type Opcao = readonly [valor: string, rotulo: string];

export interface GrupoDeOpcoes {
  rotulo: string;
  opcoes: readonly Opcao[];
}

interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  rotulo: string;
  dica?: string | undefined;
  erro?: string | undefined;
  opcoes?: readonly Opcao[];
  grupos?: readonly GrupoDeOpcoes[];
}

export function Selecao({
  rotulo,
  dica,
  erro,
  opcoes = [],
  grupos = [],
  className = "",
  ...resto
}: Props) {
  const id = useId();
  const idErro = `${id}-erro`;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="rotulo">
          {rotulo}
        </label>
        {dica !== undefined && <Dica texto={dica} />}
      </div>

      <select
        id={id}
        aria-invalid={erro !== undefined}
        {...(erro !== undefined ? { "aria-describedby": idErro } : {})}
        className={[
          "h-9 rounded-controle border bg-superficie-2 px-2 text-corpo text-texto",
          "disabled:cursor-not-allowed disabled:opacity-60",
          erro ? "border-falha" : "border-fio hover:border-fio-forte",
          className,
        ].join(" ")}
        {...resto}
      >
        {opcoes.map(([valor, texto]) => (
          <option key={valor} value={valor}>
            {texto}
          </option>
        ))}
        {grupos.map((grupo) => (
          <optgroup key={grupo.rotulo} label={grupo.rotulo}>
            {grupo.opcoes.map(([valor, texto]) => (
              <option key={valor} value={valor}>
                {texto}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {erro !== undefined && (
        <p id={idErro} role="alert" className="flex items-center gap-1 text-[12px] text-falha">
          <AlertCircle aria-hidden="true" className="size-3.5 shrink-0" />
          {erro}
        </p>
      )}
    </div>
  );
}
